// src/pages/ResetPassword/ResetPassword.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, CheckCircle, Lock } from "lucide-react";
import "./ResetPassword.css";
import AuthLayout from "../../components/AuthLayout/AuthLayout";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const validatePassword = (password) => {
  return {
    isValid: password.length >= 8,
    hasMinLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
  };
};

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (!tokenFromUrl) {
      setError("Invalid reset link. Please request a new password reset.");
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const passwordValidation = validatePassword(password);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setError("Invalid reset token. Please request a new password reset.");
      return;
    }

    if (!passwordValidation.isValid) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token,
          new_password: password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 400) {
          throw new Error("Invalid or expired reset token. Please request a new one.");
        } else {
          throw new Error(data.detail || "Failed to reset password");
        }
      }

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
      
    } catch (err) {
      if (err.name === "TypeError" && err.message.includes("fetch")) {
        setError("Cannot connect to server. Please check your internet connection.");
      } else {
        setError(err.message);
      }
      console.error("Reset password error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
    <div className="reset-password-container">
      <div className="reset-password-card">
        {!success ? (
          <>
            <div className="icon-wrapper">
              <Lock size={48} className="lock-icon" />
            </div>
            
            <h2>Reset Password</h2>
            <p className="subtitle">
              Enter your new password below
            </p>

            {error && (
              <div className="error-banner">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            <form className="reset-password-form" onSubmit={handleSubmit}>
              {/* New Password Field */}
              <div className="form-group">
                <label>New Password</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError("");
                    }}
                    onFocus={() => setShowPasswordStrength(true)}
                    onBlur={() => setTimeout(() => setShowPasswordStrength(false), 200)}
                    disabled={loading}
                    required
                    autoComplete="new-password"
                    className={error && error.includes("password") ? "input-error with-icon" : "with-icon"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle"
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {showPasswordStrength && password && (
                  <div className="password-strength">
                    <div className="strength-item">
                      <span className={passwordValidation.hasMinLength ? "check-valid" : "check-invalid"}>
                        {passwordValidation.hasMinLength ? "✓" : "○"}
                      </span>
                      <span>At least 8 characters</span>
                    </div>
                    <div className="strength-item">
                      <span className={passwordValidation.hasUpperCase ? "check-valid" : "check-invalid"}>
                        {passwordValidation.hasUpperCase ? "✓" : "○"}
                      </span>
                      <span>One uppercase letter</span>
                    </div>
                    <div className="strength-item">
                      <span className={passwordValidation.hasLowerCase ? "check-valid" : "check-invalid"}>
                        {passwordValidation.hasLowerCase ? "✓" : "○"}
                      </span>
                      <span>One lowercase letter</span>
                    </div>
                    <div className="strength-item">
                      <span className={passwordValidation.hasNumber ? "check-valid" : "check-invalid"}>
                        {passwordValidation.hasNumber ? "✓" : "○"}
                      </span>
                      <span>One number</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="form-group">
                <label>Confirm Password</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError("");
                    }}
                    disabled={loading}
                    required
                    autoComplete="new-password"
                    className={
                      error && error.includes("match")
                        ? "input-error with-icon"
                        : "with-icon"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="password-toggle"
                    tabIndex="-1"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {password && confirmPassword && password !== confirmPassword && (
                  <span className="field-error">Passwords do not match</span>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !password || !confirmPassword || !token}
                className={loading ? "loading" : ""}
              >
                {loading ? "Resetting Password..." : "Reset Password"}
              </button>
            </form>

            <div className="back-to-login">
              <Link to="/login">Back to Login</Link>
            </div>
          </>
        ) : (
          <div className="success-state">
            <div className="success-icon-wrapper">
              <CheckCircle size={64} className="success-icon" />
            </div>
            
            <h2>Password Reset Successful!</h2>
            <p className="success-message">
              Your password has been successfully reset.
            </p>
            <p className="info-text">
              Redirecting to login page in 3 seconds...
            </p>

            <Link to="/login" className="login-now-btn">
              Login Now
            </Link>
          </div>
        )}
      </div>
    </div>
    </AuthLayout>
  );
};

export default ResetPassword;